import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const FIELDS = [
    ["patronThresholdPaise", "Patron tier threshold (paise)", "number"],
    ["circleThresholdPaise", "Circle tier threshold (paise)", "number"],
    ["patronDiscountPercent", "Patron discount %", "number"],
    ["circleDiscountPercent", "Circle discount %", "number"],
    ["pointsPerRupee", "Style Points earned per ₹1", "number"],
    ["signupBonusPoints", "Signup bonus (points)", "number"],
    ["firstReviewPoints", "First review bonus (points)", "number"],
    ["firstReviewCoins", "First review bonus (coins)", "number"],
    ["coinsEarnPercent", "Coins earned per order (%)", "number"],
    ["coinsRedeemCapPercent", "Coins redemption cap (% of subtotal)", "number"],
    ["coinsExpiryDays", "Coins expire after (days)", "number"],
    ["birthdayCoins", "Birthday bonus (coins)", "number"],
    ["referralBonusCoins", "Referral bonus (coins)", "number"],
];

export default function AdminLoyaltySettings() {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    const refresh = () => api.get("/admin/loyalty/settings").then(({ data }) => setForm(data)).catch(() => setForm(null));
    useEffect(() => { refresh(); }, []);

    const save = async (e) => {
        e.preventDefault();
        if (!form) return;
        setBusy(true);
        try {
            const payload = {};
            for (const [k] of FIELDS) payload[k] = k === "pointsPerRupee" ? Number(form[k]) : parseInt(form[k], 10);
            payload.circleFreeShipping = !!form.circleFreeShipping;
            await api.patch("/admin/loyalty/settings", payload);
            toast.success("Loyalty settings saved");
            refresh();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };

    if (!form) return <div className="text-inkMuted">Loading…</div>;

    return (
        <div data-testid="admin-loyalty-settings-page">
            <span className="label-eyebrow">Programme</span>
            <h1 className="mt-2 font-display text-4xl text-inkDark">Loyalty settings</h1>
            <p className="mt-1 text-inkMuted max-w-2xl">Tier thresholds, earning rules and expiry windows for Style Points &amp; Style Coins. Changes take effect immediately; already-credited balances are unaffected.</p>

            <form onSubmit={save} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
                {FIELDS.map(([key, label, type]) => (
                    <label key={key} className="block">
                        <span className="label-eyebrow">{label}</span>
                        <input
                            type={type}
                            step={key === "pointsPerRupee" ? "0.01" : "1"}
                            value={form[key] ?? 0}
                            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                            className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                            data-testid={`loyalty-${key}-input`}
                        />
                    </label>
                ))}
                <label className="flex items-center gap-3 md:col-span-2">
                    <input type="checkbox" checked={!!form.circleFreeShipping} onChange={(e) => setForm({ ...form, circleFreeShipping: e.target.checked })} data-testid="loyalty-circle-free-shipping" />
                    <span>Circle tier gets free shipping</span>
                </label>
                <div className="md:col-span-2">
                    <button type="submit" disabled={busy} className="btn-pill text-sm disabled:opacity-50" data-testid="loyalty-save-button">{busy ? "Saving…" : "Save settings"}</button>
                </div>
            </form>
        </div>
    );
}
